import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Text, H3 } from 'components/core/Typography';
import { Button, MarkdownWrapper } from 'components/atoms';
import { Link } from 'components/core/Link';
import { Image } from 'components/core/Image';
import FeaturesItem from 'components/facility/FacilityAmenitiesAccordion/FeaturesItem';
import AccessHours from 'components/facility/FacilityAmenitiesAccordion/AccessHours';

import ScrollUpArrow from 'assets/images/icons/psp/accordion/ScrollUpArrow.svg';
import ScrollDownArrow from 'assets/images/icons/psp/accordion/ScrollDownArrow.svg';
import AccordionExpand from 'assets/images/features-amenities/accordion-expand.svg';
import AccordionContract from 'assets/images/features-amenities/accordion-contract.svg';

import './accordion.scss';

function renderAccordionContent(type, answer) {
	if (type === 'facility') {
		return <FeaturesItem featuresData={answer} />;
	}
	if (type === 'accessHours') {
		return (
			<AccessHours
				accessHours={answer.accessHours}
				officeHours={answer.officeHours}
			/>
		);
	}
	return (
		<MarkdownWrapper content={[{ type: 'faq_content', value: answer }]} />
	);
}

export function AccordionItem({
	question = '',
	answer = '',
	className = '',
	link = '',
	contentNode = null,
	titleIcon = '',
	type,
	iconType,
}) {
	const [copyExpanded, setCopyExpanded] = useState(false);

	const handleExpandCopy = useCallback(() => {
		setCopyExpanded((prev) => !prev);
	}, []);

	const expandIcon =
		iconType === 'expand' ? AccordionExpand : ScrollDownArrow;
	const contractIcon =
		iconType === 'expand' ? AccordionContract : ScrollUpArrow;

	return (
		<div
			className={classnames(className, 'accordion-item', {
				expanded: copyExpanded,
			})}
		>
			<Button
				onClick={handleExpandCopy}
				className="expand-button"
				aria-expanded={copyExpanded}
			>
				<Text
					weight="bold"
					className="item-title"
				>
					{titleIcon && (
						<Image
							src={titleIcon}
							className="title-icon"
							alt={`${question} icon`}
						/>
					)}
					<H3
						size="medium"
						className="item-title-label"
					>
						{question}
					</H3>
				</Text>

				<Image
					src={copyExpanded ? contractIcon : expandIcon}
					className="expand-button-image"
					alt={copyExpanded ? 'Collapse section' : 'Expand section'}
				/>
			</Button>

			<div
				className={classnames(
					'expand-content',
					copyExpanded
						? 'expand-content--visible'
						: 'expand-content--hidden',
				)}
				aria-hidden={!copyExpanded}
			>
				{contentNode || (
					<div className="item-answer ss-type ss-type-weight-light ss-type-last">
						{renderAccordionContent(type, answer)}
					</div>
				)}

				{link && (
					<H3
						weight="bold"
						last
						className="copy-link"
					>
						<Link href={link}>More info on {question}</Link>
					</H3>
				)}
			</div>
		</div>
	);
}

AccordionItem.propTypes = {
	question: PropTypes.string,
	answer: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object,
		PropTypes.array,
	]),
	className: PropTypes.string,
	link: PropTypes.string,
	contentNode: PropTypes.node,
	titleIcon: PropTypes.string,
	type: PropTypes.string,
	iconType: PropTypes.string,
};

export default AccordionItem;
